// Music Player Web Component
import { escapeHtml } from '../utils/html-utils.js';

class MusicPlayer extends HTMLElement {
  constructor() {
    super();
    this.platform = null;
    this.embedId = null;
    this.embedUrl = null;
    this.contentType = null;
    this.country = null;
    this.title = null;
    this.artist = null;
    this.image = null;
  }

  static get observedAttributes() {
    return ['platform', 'embed-id', 'embed-url', 'content-type', 'country', 'title', 'artist', 'image'];
  }

  connectedCallback() {
    this.syncFromAttributes();
    this.render();
  }

  syncFromAttributes() {
    this.platform = this.platform ?? this.getAttribute('platform');
    this.embedId = this.embedId ?? this.getAttribute('embed-id');
    this.embedUrl = this.embedUrl ?? this.getAttribute('embed-url');
    this.contentType = this.contentType ?? this.getAttribute('content-type');
    this.country = this.country ?? this.getAttribute('country');
    this.title = this.title ?? this.getAttribute('title');
    this.artist = this.artist ?? this.getAttribute('artist');
    this.image = this.image ?? this.getAttribute('image');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // Only update if the value actually changed
    if (oldValue === newValue) return;

    switch (name) {
    case 'platform':
      this.platform = newValue;
      break;
    case 'embed-id':
      this.embedId = newValue;
      break;
    case 'embed-url':
      this.embedUrl = newValue;
      break;
    case 'content-type':
      this.contentType = newValue;
      break;
    case 'country':
      this.country = newValue;
      break;
    case 'title':
      this.title = newValue;
      break;
    case 'artist':
      this.artist = newValue;
      break;
    case 'image':
      this.image = newValue;
      break;
    }

    // Only render if connected and this is a meaningful change
    if (this.isConnected && oldValue !== newValue) {
      // Use requestAnimationFrame to avoid render loops
      if (!this._renderScheduled) {
        this._renderScheduled = true;
        requestAnimationFrame(() => {
          this.render();
          this._renderScheduled = false;
        });
      }
    }
  }

  render() {
    if (!this.platform || !this.embedId) {
      this.innerHTML = this.renderFallback();
      return;
    }

    this.innerHTML = `
      <div class="music-player">
        ${this.renderPlayer()}
      </div>
    `;
  }

  renderPlayer() {
    switch (this.platform) {
    case 'youtube':
      return this.renderYouTube();
    case 'spotify':
      return this.renderSpotify();
    case 'tidal':
      return this.renderTidal();
    case 'apple-music':
      return this.renderAppleMusic();
    case 'soundcloud':
      return this.renderSoundCloud();
    case 'amazon-music':
      return this.renderAmazonMusic();
    default:
      return this.renderFallback();
    }
  }

  renderYouTube() {
    const embedUrl = `https://www.youtube.com/embed/${this.embedId}?rel=0&showinfo=0`;
    return `
      <iframe 
        class="music-embed youtube" 
        src="${embedUrl}" 
        frameborder="0" 
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
        allowfullscreen
        loading="lazy"
        title="YouTube Music Player">
      </iframe>
    `;
  }

  renderSpotify() {
    const embedUrl = `https://open.spotify.com/embed/${this.contentType}/${this.embedId}?utm_source=generator&theme=0`;
    return `
      <iframe 
        class="music-embed spotify ${this.contentType}" 
        src="${embedUrl}" 
        frameborder="0" 
        allowfullscreen 
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
        loading="lazy"
        title="Spotify Music Player">
      </iframe>
    `;
  }

  renderTidal() {
    const contentType = this.contentType || 'track';
    const embedUrl = `https://embed.tidal.com/${contentType}s/${this.embedId}`;
    return `
      <iframe 
        class="music-embed tidal ${escapeHtml(contentType)}" 
        src="${embedUrl}" 
        frameborder="0" 
        allowfullscreen
        loading="lazy"
        title="Tidal Music Player">
      </iframe>
    `;
  }

  renderAppleMusic() {
    const embedUrl = `https://embed.music.apple.com/${this.country}/${this.contentType}/${this.embedId}`;
    return `
      <iframe 
        class="music-embed apple-music" 
        src="${embedUrl}" 
        frameborder="0" 
        allow="autoplay *; encrypted-media *; fullscreen *" 
        sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
        loading="lazy"
        title="Apple Music Player">
      </iframe>
    `;
  }

  renderSoundCloud() {
    const embedUrl = `https://w.soundcloud.com/player/?url=${encodeURIComponent(this.embedUrl)}&color=%23ff6b9d&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=true&visual=true`;
    return `
      <iframe 
        class="music-embed soundcloud" 
        src="${embedUrl}" 
        frameborder="no" 
        scrolling="no" 
        allow="autoplay"
        loading="lazy"
        title="SoundCloud Music Player">
      </iframe>
    `;
  }

  renderAmazonMusic() {
    // Amazon Music doesn't support embeds, so show a fallback
    return this.renderFallback();
  }

  renderFallback() {
    const platformInfo = this.getPlatformInfo();

    return `
      <div class="music-fallback">
        <div class="fallback-content">
          ${this.image ? `
            <img src="${escapeHtml(this.image)}" alt="Album Art" class="fallback-image" />
          ` : `
            <div class="fallback-image" style="display: flex; align-items: center; justify-content: center; color: var(--text-muted);">
              ${platformInfo.icon}
            </div>
          `}
          <div class="fallback-details">
            ${this.title ? `
              <h4 class="fallback-title">${escapeHtml(this.title)}</h4>
            ` : ''}
            ${this.artist ? `
              <p class="fallback-artist">${escapeHtml(this.artist)}</p>
            ` : ''}
            <a href="${escapeHtml(this.embedUrl || '#')}" 
               class="fallback-platform" 
               target="_blank" 
               rel="noopener noreferrer">
              <span class="platform-icon">${platformInfo.icon}</span>
              <span>Listen on ${platformInfo.name}</span>
            </a>
          </div>
        </div>
      </div>
    `;
  }

  getPlatformInfo() {
    const platforms = {
      'youtube': { name: 'YouTube', icon: '📺' },
      'spotify': { name: 'Spotify', icon: '🎵' },
      'tidal': { name: 'Tidal', icon: '🌊' },
      'apple-music': { name: 'Apple Music', icon: '🍎' },
      'amazon-music': { name: 'Amazon Music', icon: '📦' },
      'soundcloud': { name: 'SoundCloud', icon: '☁️' },
      'deezer': { name: 'Deezer', icon: '🎶' },
      'bandcamp': { name: 'Bandcamp', icon: '🎪' }
    };

    return platforms[this.platform] || { name: 'Music Platform', icon: '🎵' };
  }


  // Public method to set all music data at once
  setMusicData(data) {
    this.platform = data.platform;
    this.embedId = data.embedId;
    this.embedUrl = data.embedUrl;
    this.contentType = data.contentType;
    this.country = data.country;
    this.title = data.title;
    this.artist = data.artist;
    this.image = data.image;
    this.render();
  }
}

// Register the component
customElements.define('music-player', MusicPlayer);

export default MusicPlayer;
